import { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Instagram } from 'lucide-react';
import api from '@/lib/api';
import { PageSEO } from '@/components/SEO';

export default function Contact() {
  const [storeSettings, setStoreSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/site-builder/store-settings');
        setStoreSettings(res.data);
      } catch (error) {
        console.error('Failed to load store settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const email = storeSettings?.store_email || '';
  const phone = storeSettings?.phone || '';
  const instagram = storeSettings?.social_links?.instagram || '';
  const address = storeSettings?.address || {};

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="contact-page">
      <PageSEO.contact />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl md:text-6xl font-medium mb-8">Contact Us</h1>
        <p className="text-lg text-stone-600 mb-12">
          We'd love to hear from you. Reach out to us through any of the channels below.
        </p>

        {loading ? (
          <div className="space-y-6">
            <div className="h-20 bg-stone-100 rounded animate-pulse" />
            <div className="h-20 bg-stone-100 rounded animate-pulse" />
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2">
            {email && (
              <a 
                href={`mailto:${email}`}
                className="flex items-start gap-4 p-6 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors group"
                data-testid="contact-email"
              >
                <div className="p-3 bg-white rounded-full group-hover:bg-primary group-hover:text-white transition-colors">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-medium mb-1">Email</h2>
                  <p className="text-stone-600">{email}</p>
                </div>
              </a>
            )}

            {phone && (
              <a 
                href={`tel:${phone.replace(/\s/g, '')}`}
                className="flex items-start gap-4 p-6 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors group"
                data-testid="contact-phone"
              >
                <div className="p-3 bg-white rounded-full group-hover:bg-primary group-hover:text-white transition-colors">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-medium mb-1">Phone</h2>
                  <p className="text-stone-600">{phone}</p>
                </div>
              </a>
            )}

            {instagram && (
              <a 
                href={instagram.startsWith('http') ? instagram : `https://instagram.com/${instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-6 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors group"
                data-testid="contact-instagram"
              >
                <div className="p-3 bg-white rounded-full group-hover:bg-primary group-hover:text-white transition-colors">
                  <Instagram className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-medium mb-1">Instagram</h2>
                  <p className="text-stone-600">{instagram.replace('https://instagram.com/', '@')}</p>
                </div>
              </a>
            )}

            {(address.street || address.city) && (
              <div className="flex items-start gap-4 p-6 bg-stone-50 rounded-lg">
                <div className="p-3 bg-white rounded-full">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-medium mb-1">Address</h2>
                  <p className="text-stone-600">
                    {address.street && <span>{address.street}<br /></span>}
                    {address.city && <span>{address.city} {address.postal_code}<br /></span>}
                    {address.country && <span>{address.country}</span>}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && !email && !phone && !instagram && (
          <div className="text-center py-12 text-stone-500">
            <p>Contact information coming soon.</p>
            <p className="text-sm mt-2">Please check back later or reach out via our social media.</p>
          </div>
        )}
      </div>
    </div>
  );
}
